import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import {
  ArrowLeft,
  Send,
  Mail,
  MessageSquare,
  Phone,
  CheckCircle,
  Loader2,
  Share2,
  Globe,
  ShoppingBag,
  CreditCard,
  Mic,
  MessageCircle,
  DollarSign,
  Package,
  Search,
  Rocket,
  Target,
  Zap,
  ArrowRight,
  AlertCircle,
  Info
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Social channels
const socialChannels = [
  { id: "youtube", name: "YouTube", icon: "📺" },
  { id: "tiktok", name: "TikTok", icon: "🎵" },
  { id: "instagram", name: "Instagram", icon: "📷" },
  { id: "facebook", name: "Facebook", icon: "👤" },
  { id: "linkedin", name: "LinkedIn", icon: "💼" },
  { id: "twitter", name: "X/Twitter", icon: "🐦" }
];

// Marketplaces
const marketplaces = [
  { id: "amazon", name: "Amazon", icon: "📦" },
  { id: "ebay", name: "eBay", icon: "🏷️" },
  { id: "etsy", name: "Etsy", icon: "🎨" },
  { id: "shopify", name: "Shopify", icon: "🛒" },
  { id: "walmart", name: "Walmart", icon: "🏪" },
  { id: "alibaba", name: "Alibaba", icon: "🌏" },
  { id: "mercari", name: "Mercari", icon: "📱" },
  { id: "poshmark", name: "Poshmark", icon: "👗" }
];

// Free deployment channels
const freeChannels = [
  { id: "craigslist", name: "Craigslist", icon: "📋" },
  { id: "facebook_marketplace", name: "FB Marketplace", icon: "🏪" },
  { id: "offerup", name: "OfferUp", icon: "📱" },
  { id: "nextdoor", name: "Nextdoor", icon: "🏘️" },
  { id: "letgo", name: "Letgo", icon: "🔄" }
];

// Lead generation (credits-based)
const leadGenChannels = [
  { id: "hunter_io", name: "Hunter.io", icon: "🎯", credits: 1, desc: "Email finder" },
  { id: "apollo_io", name: "Apollo.io", icon: "🚀", credits: 1, desc: "B2B leads" }
];

// Direct channels
const directChannels = [
  { id: "email", name: "Email", icon: <Mail className="w-5 h-5" /> },
  { id: "sms", name: "SMS/Text", icon: <Phone className="w-5 h-5" /> },
  { id: "whatsapp", name: "WhatsApp", icon: <MessageSquare className="w-5 h-5" /> }
];

// Payment options for Buy Now
const paymentOptions = [
  { id: "stripe", name: "Credit/Debit", icon: "💳" },
  { id: "paypal", name: "PayPal", icon: "🅿️" },
  { id: "apple_pay", name: "Apple Pay", icon: "🍎" },
  { id: "google_pay", name: "Google Pay", icon: "🔷" },
  { id: "klarna", name: "Klarna", icon: "🟢" },
  { id: "crypto", name: "Crypto", icon: "₿" },
  { id: "invoice", name: "Invoice", icon: "📄" }
];

export default function Broadcast() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [user, setUser] = useState(null);
  const [project, setProject] = useState(null);
  
  const [selectedSocial, setSelectedSocial] = useState([]);
  const [selectedMarketplaces, setSelectedMarketplaces] = useState([]);
  const [selectedFree, setSelectedFree] = useState([]);
  const [selectedLeadGen, setSelectedLeadGen] = useState([]);
  const [selectedDirect, setSelectedDirect] = useState([]);
  const [selectedPayments, setSelectedPayments] = useState(["stripe"]);
  
  const [sending, setSending] = useState(false);
  
  // Communication options
  const [pttEnabled, setPttEnabled] = useState(false);
  const [textMeEnabled, setTextMeEnabled] = useState(false);
  const [textMeNumber, setTextMeNumber] = useState("");
  
  // Smart Routing
  const [smartRoutingEnabled, setSmartRoutingEnabled] = useState(true);
  const [smartRouteContacts, setSmartRouteContacts] = useState([]);
  const [smartRouteMessage, setSmartRouteMessage] = useState("");
  const [smartRouteResults, setSmartRouteResults] = useState(null);
  
  // Buy Now options
  const [buyNowEnabled, setBuyNowEnabled] = useState(false);
  const [buyNowPrice, setBuyNowPrice] = useState("");
  
  // Direct channel configs
  const [emailConfig, setEmailConfig] = useState({ recipients: "", subject: "", message: "" });
  const [smsConfig, setSmsConfig] = useState({ recipients: "", message: "" });
  const [whatsappConfig, setWhatsappConfig] = useState({ recipients: "", message: "" });
  
  // Shipping
  const [shippingEnabled, setShippingEnabled] = useState(false);
  const [shippingMarkup, setShippingMarkup] = useState(15);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    if (projectId) {
      fetchProject();
    }
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      console.error("Failed to load project");
    }
  };

  const toggleItem = (id, list, setList) => {
    if (list.includes(id)) {
      setList(list.filter(c => c !== id));
    } else {
      setList([...list, id]);
    }
  };

  const calculateCreditsNeeded = () => {
    let credits = 0;
    selectedLeadGen.forEach(id => {
      const channel = leadGenChannels.find(c => c.id === id);
      if (channel) credits += channel.credits;
    });
    return credits;
  };

  const handleBroadcast = async () => {
    const totalSelected = selectedSocial.length + selectedMarketplaces.length + 
                         selectedFree.length + selectedLeadGen.length + selectedDirect.length;
    
    if (totalSelected === 0) {
      toast.error("Select at least one channel");
      return;
    }

    const creditsNeeded = calculateCreditsNeeded();
    if (user && creditsNeeded > user.credits) {
      toast.error(`Insufficient credits. Need ${creditsNeeded}, have ${user.credits}`);
      return;
    }

    setSending(true);
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/broadcast`,
        {
          project_id: projectId,
          channels: selectedSocial,
          marketplaces: selectedMarketplaces,
          direct_channels: selectedDirect,
          email_config: selectedDirect.includes("email") ? emailConfig : null,
          sms_config: selectedDirect.includes("sms") ? smsConfig : null,
          whatsapp_config: selectedDirect.includes("whatsapp") ? whatsappConfig : null,
          communication_options: {
            ptt_enabled: pttEnabled,
            text_me_enabled: textMeEnabled,
            text_me_number: textMeNumber
          },
          buy_now_enabled: buyNowEnabled,
          buy_now_config: buyNowEnabled ? {
            price: buyNowPrice,
            payment_methods: selectedPayments,
            shipping_enabled: shippingEnabled,
            shipping_markup: shippingMarkup
          } : null,
          ptt_enabled: pttEnabled,
          text_me_enabled: textMeEnabled,
          text_me_number: textMeNumber,
          smart_routing_enabled: smartRoutingEnabled
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      
      toast.success(`Video queued for ${totalSelected} channel(s)!`);
      
      // Update local user credits
      if (user && creditsNeeded > 0) {
        const updatedUser = { ...user, credits: user.credits - creditsNeeded };
        setUser(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Broadcast failed");
    } finally {
      setSending(false);
    }
  };

  // Smart Route contacts
  const handleSmartRoute = async () => {
    if (smartRouteContacts.length === 0) {
      toast.error("Add at least one contact");
      return;
    }
    if (!smartRouteMessage.trim()) {
      toast.error("Enter a message");
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/broadcast/smart-route`,
        {
          contacts: smartRouteContacts,
          message: smartRouteMessage,
          project_id: projectId,
          fallback_to_email: true
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      
      setSmartRouteResults(response.data);
      toast.success(`Sent to ${response.data.successful} of ${response.data.total_contacts} contacts`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Smart routing failed");
    } finally {
      setSending(false);
    }
  };

  const addSmartRouteContact = () => {
    setSmartRouteContacts([
      ...smartRouteContacts,
      {
        id: crypto.randomUUID(),
        name: "",
        email: "",
        phone: "",
        phone_verified: false,
        email_verified: false
      }
    ]);
  };

  const updateSmartRouteContact = (index, field, value) => {
    const updated = [...smartRouteContacts];
    updated[index] = { ...updated[index], [field]: value };
    setSmartRouteContacts(updated);
  };

  const removeSmartRouteContact = (index) => {
    setSmartRouteContacts(smartRouteContacts.filter((_, i) => i !== index));
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/dashboard")}
            className="text-text-secondary hover:text-text-primary"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <span className="font-heading font-bold text-text-primary">Broadcast Center</span>
          {user && (
            <div className="flex items-center gap-2 text-sm">
              <CreditCard className="w-4 h-4 text-primary" />
              <span className="text-primary font-bold">{user.credits}</span>
              <span className="text-text-muted">credits</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="space-y-8">
          {/* Header */}
          <div className="text-center">
            <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Share2 className="w-7 h-7 text-primary" />
            </div>
            <h1 className="font-heading font-bold text-2xl text-text-primary mb-2">
              Broadcast Your Video
            </h1>
            <p className="text-text-secondary text-sm">
              Share to social media, marketplaces, or send directly to contacts
            </p>
          </div>

          {/* Social Channels */}
          <div>
            <Label className="text-text-primary font-medium mb-3 flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Social Media
            </Label>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {socialChannels.map((channel) => (
                <Card
                  key={channel.id}
                  onClick={() => toggleItem(channel.id, selectedSocial, setSelectedSocial)}
                  className={`cursor-pointer transition-all ${
                    selectedSocial.includes(channel.id)
                      ? 'bg-primary/10 border-primary'
                      : 'bg-background-paper border-border hover:border-primary/50'
                  }`}
                >
                  <CardContent className="p-3 text-center">
                    <span className="text-xl">{channel.icon}</span>
                    <div className="text-xs text-text-primary mt-1">{channel.name}</div>
                    {selectedSocial.includes(channel.id) && (
                      <CheckCircle className="w-3 h-3 text-primary mx-auto mt-1" />
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Marketplaces */}
          <div>
            <Label className="text-text-primary font-medium mb-3 flex items-center gap-2">
              <ShoppingBag className="w-4 h-4" />
              Marketplaces
            </Label>
            <div className="grid grid-cols-4 gap-2">
              {marketplaces.map((market) => (
                <Card
                  key={market.id}
                  onClick={() => toggleItem(market.id, selectedMarketplaces, setSelectedMarketplaces)}
                  className={`cursor-pointer transition-all ${
                    selectedMarketplaces.includes(market.id)
                      ? 'bg-primary/10 border-primary'
                      : 'bg-background-paper border-border hover:border-primary/50'
                  }`}
                >
                  <CardContent className="p-3 text-center">
                    <span className="text-xl">{market.icon}</span>
                    <div className="text-xs text-text-primary mt-1">{market.name}</div>
                    {selectedMarketplaces.includes(market.id) && (
                      <CheckCircle className="w-3 h-3 text-primary mx-auto mt-1" />
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Free Channels */}
          <div>
            <Label className="text-text-primary font-medium mb-3 flex items-center gap-2">
              <Target className="w-4 h-4" />
              Free Deployment Channels
            </Label>
            <div className="grid grid-cols-5 gap-2">
              {freeChannels.map((channel) => (
                <Card
                  key={channel.id}
                  onClick={() => toggleItem(channel.id, selectedFree, setSelectedFree)}
                  className={`cursor-pointer transition-all ${
                    selectedFree.includes(channel.id)
                      ? 'bg-accent-success/10 border-accent-success'
                      : 'bg-background-paper border-border hover:border-accent-success/50'
                  }`}
                >
                  <CardContent className="p-3 text-center">
                    <span className="text-xl">{channel.icon}</span>
                    <div className="text-xs text-text-primary mt-1">{channel.name}</div>
                    <div className="text-[10px] text-accent-success">FREE</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Lead Gen (Credits) */}
          <div>
            <Label className="text-text-primary font-medium mb-3 flex items-center gap-2">
              <Rocket className="w-4 h-4" />
              Lead Generation (Credits)
            </Label>
            <div className="grid grid-cols-2 gap-3">
              {leadGenChannels.map((channel) => (
                <Card
                  key={channel.id}
                  onClick={() => toggleItem(channel.id, selectedLeadGen, setSelectedLeadGen)}
                  className={`cursor-pointer transition-all ${
                    selectedLeadGen.includes(channel.id)
                      ? 'bg-accent-warning/10 border-accent-warning'
                      : 'bg-background-paper border-border hover:border-accent-warning/50'
                  }`}
                >
                  <CardContent className="p-4 flex items-center gap-3">
                    <span className="text-2xl">{channel.icon}</span>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-text-primary">{channel.name}</div>
                      <div className="text-xs text-text-muted">{channel.desc}</div>
                    </div>
                    <div className="text-sm text-accent-warning font-bold">{channel.credits} credit</div>
                    {selectedLeadGen.includes(channel.id) && (
                      <CheckCircle className="w-5 h-5 text-accent-warning" />
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Direct Channels */}
          <div>
            <Label className="text-text-primary font-medium mb-3 flex items-center gap-2">
              <Send className="w-4 h-4" />
              Direct Send
            </Label>
            <div className="grid grid-cols-3 gap-2">
              {directChannels.map((channel) => (
                <Card
                  key={channel.id}
                  onClick={() => toggleItem(channel.id, selectedDirect, setSelectedDirect)}
                  className={`cursor-pointer transition-all ${
                    selectedDirect.includes(channel.id)
                      ? 'bg-primary/10 border-primary'
                      : 'bg-background-paper border-border hover:border-primary/50'
                  }`}
                >
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="text-primary">{channel.icon}</div>
                    <div className="text-sm text-text-primary">{channel.name}</div>
                    {selectedDirect.includes(channel.id) && (
                      <CheckCircle className="w-4 h-4 text-primary ml-auto" />
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Smart Routing Section */}
          <div className="p-4 rounded-xl bg-gradient-to-br from-primary/5 to-accent-neon/5 border border-primary/20 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-text-primary font-medium flex items-center gap-2">
                  <Zap className="w-4 h-4 text-primary" />
                  Smart Communication Routing
                </Label>
                <p className="text-xs text-text-muted mt-1">
                  Automatically selects the best channel for each contact
                </p>
              </div>
              <Switch checked={smartRoutingEnabled} onCheckedChange={setSmartRoutingEnabled} />
            </div>

            {smartRoutingEnabled && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="space-y-4"
              >
                {/* Routing Logic Display */}
                <div className="p-3 rounded-lg bg-background border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <Info className="w-4 h-4 text-primary" />
                    <span className="text-xs font-medium text-text-primary">Routing Priority</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="px-2 py-1 rounded bg-accent-success/20 text-accent-success flex items-center gap-1">
                      <Phone className="w-3 h-3" /> Phone Verified → SMS
                    </span>
                    <ArrowRight className="w-3 h-3 text-text-muted" />
                    <span className="px-2 py-1 rounded bg-primary/20 text-primary flex items-center gap-1">
                      <Mail className="w-3 h-3" /> Email Verified → Email
                    </span>
                    <ArrowRight className="w-3 h-3 text-text-muted" />
                    <span className="px-2 py-1 rounded bg-accent-warning/20 text-accent-warning flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" /> Default → WhatsApp
                    </span>
                    <ArrowRight className="w-3 h-3 text-text-muted" />
                    <span className="px-2 py-1 rounded bg-accent-error/20 text-accent-error flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> Fallback → Email
                    </span>
                  </div>
                </div>

                {/* Contacts List */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-text-muted text-xs">Contacts ({smartRouteContacts.length})</Label>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={addSmartRouteContact}
                      className="text-xs h-7"
                    >
                      + Add Contact
                    </Button>
                  </div>
                  
                  {smartRouteContacts.map((contact, index) => (
                    <div key={contact.id} className="p-3 rounded-lg bg-background border border-border space-y-2">
                      <div className="flex items-center gap-2">
                        <Input
                          value={contact.name}
                          onChange={(e) => updateSmartRouteContact(index, "name", e.target.value)}
                          placeholder="Name"
                          className="bg-background-subtle border-border text-text-primary text-sm h-8"
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => removeSmartRouteContact(index)}
                          className="text-accent-error h-8 px-2"
                        >
                          ✕
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex items-center gap-2">
                          <Input
                            value={contact.phone}
                            onChange={(e) => updateSmartRouteContact(index, "phone", e.target.value)}
                            placeholder="Phone"
                            className="bg-background-subtle border-border text-text-primary text-sm h-8 flex-1"
                          />
                          <label className="flex items-center gap-1 text-xs text-text-muted whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={contact.phone_verified}
                              onChange={(e) => updateSmartRouteContact(index, "phone_verified", e.target.checked)}
                              className="rounded"
                            />
                            Verified
                          </label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            value={contact.email}
                            onChange={(e) => updateSmartRouteContact(index, "email", e.target.value)}
                            placeholder="Email"
                            className="bg-background-subtle border-border text-text-primary text-sm h-8 flex-1"
                          />
                          <label className="flex items-center gap-1 text-xs text-text-muted whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={contact.email_verified}
                              onChange={(e) => updateSmartRouteContact(index, "email_verified", e.target.checked)}
                              className="rounded"
                            />
                            Verified
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Message */}
                <div>
                  <Label className="text-text-muted text-xs mb-1 block">Message</Label>
                  <Textarea
                    value={smartRouteMessage}
                    onChange={(e) => setSmartRouteMessage(e.target.value)}
                    placeholder="Your message to send via smart routing..."
                    className="bg-background border-border text-text-primary"
                    rows={2}
                  />
                </div>

                {/* Send Button */}
                <Button
                  onClick={handleSmartRoute}
                  disabled={sending || smartRouteContacts.length === 0}
                  className="w-full bg-gradient-to-r from-primary to-accent-neon hover:opacity-90 text-white"
                >
                  {sending ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Routing...</>
                  ) : (
                    <><Zap className="w-4 h-4 mr-2" />Send via Smart Routing</>
                  )}
                </Button>

                {/* Results */}
                {smartRouteResults && (
                  <div className="p-3 rounded-lg bg-background border border-border">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-text-primary">Results</span>
                      <span className="text-xs text-text-muted">
                        {smartRouteResults.successful}/{smartRouteResults.total_contacts} sent
                      </span>
                    </div>
                    <div className="space-y-1">
                      {smartRouteResults.results.map((result, i) => (
                        <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-border last:border-0">
                          <span className="text-text-primary">{result.contact_name || `Contact ${i + 1}`}</span>
                          <div className="flex items-center gap-2">
                            {result.status === "sent" ? (
                              <span className="text-accent-success flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                {result.channel_used.toUpperCase()}
                                {result.fallback_used && <span className="text-text-muted">(fallback)</span>}
                              </span>
                            ) : (
                              <span className="text-accent-error flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                Failed
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </div>

          {/* Communication Options */}
          <div className="p-4 rounded-xl bg-background-paper border border-border space-y-4">
            <Label className="text-text-primary font-medium flex items-center gap-2">
              <MessageCircle className="w-4 h-4" />
              Communication Options
            </Label>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-background border border-border">
                <div className="flex items-center gap-2">
                  <Mic className="w-4 h-4 text-primary" />
                  <span className="text-sm text-text-primary">PTT (Push to Talk)</span>
                </div>
                <Switch checked={pttEnabled} onCheckedChange={setPttEnabled} />
              </div>
              
              <div className="flex items-center justify-between p-3 rounded-lg bg-background border border-border">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-primary" />
                  <span className="text-sm text-text-primary">Text Me Option</span>
                </div>
                <Switch checked={textMeEnabled} onCheckedChange={setTextMeEnabled} />
              </div>
            </div>

            {textMeEnabled && (
              <div>
                <Label className="text-text-muted text-xs mb-1 block">Your Phone Number</Label>
                <Input
                  value={textMeNumber}
                  onChange={(e) => setTextMeNumber(e.target.value)}
                  placeholder="+1234567890"
                  className="bg-background border-border text-text-primary"
                />
              </div>
            )}
          </div>

          {/* Buy Now Options */}
          <div className="p-4 rounded-xl bg-background-paper border border-border space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-text-primary font-medium flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Buy Now Button
              </Label>
              <Switch checked={buyNowEnabled} onCheckedChange={setBuyNowEnabled} />
            </div>

            {buyNowEnabled && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="space-y-4"
              >
                <div>
                  <Label className="text-text-muted text-xs mb-1 block">Product Price</Label>
                  <Input
                    type="number"
                    value={buyNowPrice}
                    onChange={(e) => setBuyNowPrice(e.target.value)}
                    placeholder="99.99"
                    className="bg-background border-border text-text-primary"
                  />
                </div>

                <div>
                  <Label className="text-text-muted text-xs mb-2 block">Payment Methods</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {paymentOptions.map((option) => (
                      <div
                        key={option.id}
                        onClick={() => toggleItem(option.id, selectedPayments, setSelectedPayments)}
                        className={`p-2 rounded-lg border cursor-pointer text-center transition-all ${
                          selectedPayments.includes(option.id)
                            ? 'bg-primary/10 border-primary'
                            : 'bg-background border-border hover:border-primary/50'
                        }`}
                      >
                        <span className="text-lg">{option.icon}</span>
                        <div className="text-xs text-text-primary mt-1">{option.name}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Shipping Options */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-background border border-border">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-primary" />
                    <span className="text-sm text-text-primary">Enable Shipping (ShipStation)</span>
                  </div>
                  <Switch checked={shippingEnabled} onCheckedChange={setShippingEnabled} />
                </div>

                {shippingEnabled && (
                  <div className="p-3 rounded-lg bg-background border border-border">
                    <Label className="text-text-muted text-xs mb-2 block">Shipping Markup (%)</Label>
                    <div className="flex items-center gap-3">
                      <Input
                        type="number"
                        value={shippingMarkup}
                        onChange={(e) => setShippingMarkup(Number(e.target.value))}
                        className="bg-background-subtle border-border text-text-primary w-24"
                      />
                      <span className="text-text-muted text-sm">% markup on shipping rates</span>
                    </div>
                    <p className="text-xs text-text-muted mt-2">
                      Includes fraud analytics and white-label shipping labels
                    </p>
                  </div>
                )}
              </motion.div>
            )}
          </div>

          {/* Direct Channel Configs */}
          {selectedDirect.includes("email") && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="p-4 rounded-xl bg-background-paper border border-border space-y-3"
            >
              <Label className="text-text-primary font-medium flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email Settings
              </Label>
              <Input
                value={emailConfig.recipients}
                onChange={(e) => setEmailConfig({...emailConfig, recipients: e.target.value})}
                placeholder="email1@example.com, email2@example.com"
                className="bg-background border-border text-text-primary"
              />
              <Input
                value={emailConfig.subject}
                onChange={(e) => setEmailConfig({...emailConfig, subject: e.target.value})}
                placeholder="Email subject"
                className="bg-background border-border text-text-primary"
              />
              <Textarea
                value={emailConfig.message}
                onChange={(e) => setEmailConfig({...emailConfig, message: e.target.value})}
                placeholder="Your message..."
                className="bg-background border-border text-text-primary"
                rows={2}
              />
            </motion.div>
          )}

          {selectedDirect.includes("sms") && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="p-4 rounded-xl bg-background-paper border border-border space-y-3"
            >
              <Label className="text-text-primary font-medium flex items-center gap-2">
                <Phone className="w-4 h-4" />
                SMS Settings
              </Label>
              <Input
                value={smsConfig.recipients}
                onChange={(e) => setSmsConfig({...smsConfig, recipients: e.target.value})}
                placeholder="+1234567890, +0987654321"
                className="bg-background border-border text-text-primary"
              />
              <Textarea
                value={smsConfig.message}
                onChange={(e) => setSmsConfig({...smsConfig, message: e.target.value})}
                placeholder="Your SMS message..."
                className="bg-background border-border text-text-primary"
                rows={2}
              />
            </motion.div>
          )}

          {selectedDirect.includes("whatsapp") && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="p-4 rounded-xl bg-background-paper border border-border space-y-3"
            >
              <Label className="text-text-primary font-medium flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                WhatsApp Settings
              </Label>
              <Input
                value={whatsappConfig.recipients}
                onChange={(e) => setWhatsappConfig({...whatsappConfig, recipients: e.target.value})}
                placeholder="+1234567890, +0987654321"
                className="bg-background border-border text-text-primary"
              />
              <Textarea
                value={whatsappConfig.message}
                onChange={(e) => setWhatsappConfig({...whatsappConfig, message: e.target.value})}
                placeholder="Your WhatsApp message..."
                className="bg-background border-border text-text-primary"
                rows={2}
              />
            </motion.div>
          )}

          {/* Summary & Send */}
          <div className="p-4 rounded-xl bg-background-subtle border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-text-primary font-medium">Selected Channels</span>
              <span className="text-primary font-bold">
                {selectedSocial.length + selectedMarketplaces.length + selectedFree.length + selectedLeadGen.length + selectedDirect.length}
              </span>
            </div>
            {calculateCreditsNeeded() > 0 && (
              <div className="flex items-center justify-between mb-4 text-sm">
                <span className="text-text-muted">Credits needed</span>
                <span className="text-accent-warning font-bold">{calculateCreditsNeeded()}</span>
              </div>
            )}
            <Button
              onClick={handleBroadcast}
              disabled={sending || (selectedSocial.length + selectedMarketplaces.length + selectedFree.length + selectedLeadGen.length + selectedDirect.length) === 0}
              className="w-full bg-accent-success hover:bg-accent-success/90 text-white py-6"
            >
              {sending ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Broadcasting...</>
              ) : (
                <><Send className="w-5 h-5 mr-2" />Broadcast Video</>
              )}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
